import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router";
import { api } from "../api/client";
import { qk, useProject, useTask, useTimeline } from "../api/queries";
import type { PatchTaskInput } from "../api/types";
import { Icon } from "../components/Icon";
import { ErrorBox, Spinner } from "../components/Spinner";
import { EditableMarkdown, EditableTitle } from "../components/task/Editable";
import { ReviewPanel, TaskDiffSection, OpenRichDiff } from "../components/task/ReviewPanel";
import { TaskActions } from "../components/task/TaskActions";
import { TaskProperties } from "../components/task/TaskProperties";
import { Timeline } from "../components/timeline/Timeline";

/** Task detail rendered as a Notion-style side peek over the board. */
export function TaskPeek() {
  const { pid = "", tid = "" } = useParams();
  const navigate = useNavigate();
  const close = () => navigate(`/p/${pid}`);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape" || e.defaultPrevented) return;
      const el = document.activeElement as HTMLElement | null;
      if (el && /^(INPUT|TEXTAREA|SELECT)$/.test(el.tagName)) return;
      if (document.querySelector(".modal-overlay")) return;
      navigate(`/p/${pid}`);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [navigate, pid]);

  return (
    <aside className="peek" aria-label="Task detail">
      <div className="peek-toolbar">
        <button className="btn-icon" onClick={close} aria-label="Close" title="Close (Esc)">
          <Icon name="x" />
        </button>
      </div>
      <TaskDetailBody pid={pid} tid={tid} />
    </aside>
  );
}

function TaskDetailBody({ pid, tid }: { pid: string; tid: string }) {
  const qc = useQueryClient();
  const { data: task, isLoading, error } = useTask(tid);
  const project = useProject(pid);
  const timeline = useTimeline(tid);

  const patch = useMutation({
    meta: { inlineError: true },
    mutationFn: (p: PatchTaskInput) => api.patchTask(tid, p),
    onSettled: () => {
      qc.invalidateQueries({ queryKey: qk.task(tid) });
      qc.invalidateQueries({ queryKey: qk.tasks(pid) });
    },
  });

  if (isLoading) {
    return (
      <div className="peek-body">
        <Spinner label="Loading task…" />
      </div>
    );
  }
  if (error || !task) {
    return (
      <div className="peek-body">
        <ErrorBox error={error ?? "Task not found"} />
      </div>
    );
  }

  const hasCode = !!task.branch && !["human"].includes(task.stages.find((s) => s.key === task.stage_key)?.kind ?? "");

  return (
    <div className="peek-body">
      <EditableTitle value={task.title} prefix={`#${task.number}`} onSave={(title) => patch.mutateAsync({ title })} />
      <TaskActions task={task} project={project.data} />
      <TaskProperties task={task} />

      {task.status === "human_review" && <ReviewPanel task={task} timeline={timeline.data} />}

      <EditableMarkdown
        label="Description"
        value={task.description}
        onSave={(description) => patch.mutateAsync({ description })}
      />
      <EditableMarkdown
        label="Acceptance criteria"
        value={task.acceptance_criteria}
        onSave={(acceptance_criteria) => patch.mutateAsync({ acceptance_criteria })}
      />

      {task.status !== "human_review" && hasCode && (
        <DiffDisclosure taskId={task.id} projectId={task.project_id} />
      )}

      <Timeline task={task} events={timeline.data} isLoading={timeline.isLoading} error={timeline.error} />
    </div>
  );
}

function DiffDisclosure({ taskId, projectId }: { taskId: string; projectId: string }) {
  const [open, setOpen] = useState(false);
  return (
    <section className="doc-section">
      <div className="diff-disclosure-head">
        <button className="section-toggle" onClick={() => setOpen((o) => !o)} aria-expanded={open}>
          <Icon name={open ? "chevronDown" : "chevronRight"} size={12} />
          <span className="section-label">Diff against base</span>
        </button>
        <OpenRichDiff projectId={projectId} taskId={taskId} />
      </div>
      {open && <TaskDiffSection taskId={taskId} />}
    </section>
  );
}
